import React from "react";
import MuiTab from "@material-ui/core/Tab";
import PropTypes from "prop-types";

/**
 * Tab component wrapper for individual tabs within Tabs component.
 * Wraps Material-UI Tab with full API support.
 * 
 * @component
 * @example
 * // Basic usage
 * <Tab label="Profile" />
 * 
 * @example
 * // With icon
 * <Tab label="Profile" icon={<PersonIcon />} />
 * 
 * @example
 * // With custom props
 * <Tab 
 *   label="Users" 
 *   disabled={isLoading}
 *   id="user-tab"
 *   aria-controls="user-tabpanel"
 * />
 */
const Tab = ({ children, ...props }) => {
  return <MuiTab {...props}>{children}</MuiTab>;
};

Tab.propTypes = {
  /**
   * The content of the component
   */
  children: PropTypes.node,
  /**
   * The label element
   */
  label: PropTypes.node,
  /**
   * The icon element
   */
  icon: PropTypes.oneOfType([PropTypes.element, PropTypes.string]),
  /**
   * The position of the icon relative to the label
   */
  iconPosition: PropTypes.oneOf(["start", "end", "top", "bottom"]),
  /**
   * If true, the tab will be disabled
   */
  disabled: PropTypes.bool,
  /**
   * If true, the tab indicator will not be shown
   */
  disableRipple: PropTypes.bool,
  /**
   * If true, the keyboard focus ripple will be disabled
   */
  disableFocusRipple: PropTypes.bool,
  /**
   * The value of the tab. If not provided, falls back to the index
   */
  value: PropTypes.any,
  /**
   * You can wrap a node
   */
  wrapped: PropTypes.bool,
  /**
   * The component used for the root node
   */
  component: PropTypes.elementType,
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles
   */
  sx: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.bool])
    ),
    PropTypes.func,
    PropTypes.object,
  ]),
  /**
   * @ignore
   */
  id: PropTypes.string,
  /**
   * The id of the element that contains the content for this tab (accessibility)
   */
  "aria-controls": PropTypes.string,
};

export default Tab;

